/**
 * Workspace API Service
 * Simple wrapper for Trimble Connect Workspace API
 */

import { connect } from "trimble-connect-workspace-api";
import type {
  ProjectInfo,
  ProjectSettings,
  UserInfo,
  UserDetails,
  HostInfo,
} from "../types/workspace";

// Re-export the proper event types from the workspace API
type EventId = string;

interface WorkspaceEventData {
  data: unknown;
  action?: string;
  origin?: { isSelf?: boolean };
}

export interface WorkspaceAPI {
  extension: {
    requestPermission: (type: string) => Promise<string>;
    getHost: () => Promise<HostInfo>;
    host?: { name: string; version?: string };
    configure: (config: any) => Promise<boolean>;
    broadcast: (message: any) => Promise<boolean>;
    setStatusMessage: (message: string) => Promise<boolean>;
    getStatusMessage: () => Promise<string>;
    getPermission: (permission: string) => Promise<string>;
    goToSettings: () => Promise<boolean>;
    goTo: (routeKey: string, params?: any) => Promise<boolean>;
    requestFocus: () => Promise<boolean>;
    preventClosing: (text?: string) => Promise<void>;
    onAttachmentClicked: (
      type: string,
      fileId: string,
      versionId: string,
      url: string,
    ) => Promise<void>;
  };
  project: {
    getProject: () => Promise<ProjectInfo>;
    getSettings: () => Promise<ProjectSettings>;
    getMembers: () => Promise<UserDetails[]>;
    setProject: (projectId: string) => Promise<void>;
    getCurrentProject: () => Promise<ProjectInfo>;
  };
  user: {
    getUser: () => Promise<UserInfo>;
    getSettings: () => Promise<unknown>;
    getUserSettings: () => Promise<UserInfo>;
  };
  viewer: {
    [key: string]: any;
  };
  view: {
    [key: string]: any;
  };
  markup: {
    [key: string]: any;
  };
  embed: {
    [key: string]: any;
  };
  ui: {
    [key: string]: any;
  };
  dataTable: {
    [key: string]: any;
  };
  propertyPanel: {
    [key: string]: any;
  };
  modelsPanel: {
    [key: string]: any;
  };
  [key: string]: any;
}

let apiInstance: WorkspaceAPI | null = null;
let accessToken: string = "";
let tokenExpiration: number = 0;

// Generic event pub/sub for all workspace events
type WorkspaceEventListener = (event: string, data: unknown) => void;
const eventListeners = new Set<WorkspaceEventListener>();

/**
 * Subscribe to all workspace events from the host.
 * Callback receives the event name and event data.
 * Returns an unsubscribe function.
 *
 * Events include:
 * - extension.command, extension.accessToken, extension.broadcast
 * - viewer.onSelectionChanged, viewer.onCameraChanged, viewer.onModelStateChanged
 * - viewer.onPicked, viewer.onToolChanged, viewer.onSettingsChanged
 * - project.onChanged, project.onSettingsChanged
 * - datatable.onConfigChanged, propertyPanel.onModelChanged
 * - and more
 */
export const onWorkspaceEvent = (
  listener: WorkspaceEventListener,
): (() => void) => {
  eventListeners.add(listener);
  return () => {
    eventListeners.delete(listener);
  };
};

/**
 * Subscribe to a specific workspace event by name.
 * Returns an unsubscribe function.
 */
export const onCommand = (listener: (data: unknown) => void): (() => void) => {
  const wrapper: WorkspaceEventListener = (event, data) => {
    if (event === "extension.command") listener(data);
  };
  eventListeners.add(wrapper);
  return () => {
    eventListeners.delete(wrapper);
  };
};

/**
 * Initialize connection to Workspace API and get access token
 */
export const initWorkspaceAPI = async (): Promise<string> => {
  try {
    const api = await connect(
      window.parent,
      (event: EventId, args: WorkspaceEventData) => {
        // Notify all subscribers
        eventListeners.forEach((fn) => fn(event, args.data));

        // Handle internal token management
        if (
          event === "extension.accessToken" &&
          typeof args.data === "string"
        ) {
          accessToken = args.data;
          tokenExpiration = extractTokenExpiration(args.data);
        }
      },
    );

    apiInstance = api as unknown as WorkspaceAPI;

    // Request access token
    const token = await api.extension.requestPermission("accesstoken");
    accessToken = token;
    tokenExpiration = extractTokenExpiration(token);

    return token;
  } catch (error) {
    console.error("Failed to initialize workspace API:", error);
    throw error;
  }
};

/**
 * Extract expiration time from JWT token
 */
const extractTokenExpiration = (token: string): number => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp;
  } catch (error) {
    console.error("Failed to parse token:", error);
    return 0;
  }
};

/**
 * Get the current access token
 */
export const getAccessToken = (): string => accessToken;

/**
 * Refresh the access token if it is expired or about to expire (within 2 minutes).
 * Returns the current valid token or a freshly requested one.
 */
export const refreshAccessToken = async (): Promise<string> => {
  const REFRESH_BUFFER_SECONDS = 120;
  const now = Date.now() / 1000;
  if (accessToken && tokenExpiration - now > REFRESH_BUFFER_SECONDS) {
    return accessToken;
  }
  if (!apiInstance) {
    throw new Error("Workspace API not initialized");
  }
  const token = await apiInstance.extension.requestPermission("accesstoken");
  accessToken = token;
  tokenExpiration = extractTokenExpiration(token);
  return token;
};

/**
 * Get the API instance
 */
export const getAPI = (): WorkspaceAPI | null => apiInstance;

/**
 * Check if token is still valid
 */
export const isTokenValid = (): boolean => {
  if (!accessToken) return false;
  const now = Date.now() / 1000;
  return tokenExpiration > now;
};

/**
 * Get time until token expiration (in seconds)
 */
export const getTokenTimeRemaining = (): number => {
  if (!tokenExpiration) return 0;
  const now = Date.now() / 1000;
  return Math.max(0, tokenExpiration - now);
};

/**
 * Get host info (parent application name/type)
 */
export const getHostInfo = async (): Promise<HostInfo | null> => {
  if (!apiInstance) return null;

  const result: HostInfo = { name: "" };

  try {
    const hostData = await apiInstance.extension.getHost();
    if (hostData?.name) {
      result.name = hostData.name;
    }
  } catch {
    // Fallback to deprecated host property
    const hostProp = apiInstance.extension.host;
    if (hostProp?.name) {
      result.name = hostProp.name;
    }
    if (hostProp?.version) {
      result.version = hostProp.version;
    }
  }

  return result;
};
